import { Account } from '@app/domain/entities';
import type { IAccountRepository } from '@app/domain/ports';
import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';

interface AccountRow extends Omit<Account, 'hidden'> {
  hidden: number;
}

export class AccountRepository extends BaseSqliteRepository implements IAccountRepository {
  async findAll(options?: { withHidden?: boolean }): Promise<Account[]> {
    let where = ' WHERE is_deleted = 0';
    const filterHidden = !options?.withHidden;
    if (filterHidden) {
      where += ' AND is_hidden = 0';
    }

    const q = `SELECT _id as id, name as name, position as position, CAST(is_hidden AS INTEGER) as hidden FROM accounts ${where} ORDER BY position ASC`;
    const rows = await this.query<AccountRow>(q);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async getById(id: number): Promise<Account | null> {
    const row = await this.queryOne<AccountRow>(
      'SELECT _id as id, name as name, position as position, CAST(is_hidden AS INTEGER) as hidden FROM accounts WHERE _id = ? AND is_deleted = 0',
      [id],
    );
    return row ? this.mapRowToEntity(row) : null;
  }

  async insert(account: Omit<Account, 'id'>): Promise<number> {
    const result = await this.queryOne<{ id: number }>(
      'INSERT INTO accounts (name, position, is_hidden) VALUES (?, ?, ?) RETURNING _id as id',
      [account.name, account.position, account.hidden ? 1 : 0],
    );
    if (!result) throw new SqliteQueryError('INSERT INTO accounts', [], 'Failed to insert account');
    return result.id;
  }

  async update(account: Account): Promise<void> {
    await this.execute(
      "UPDATE accounts SET name = ?, position = ?, is_hidden = ?, updated_at = strftime('%s','now') WHERE _id = ? AND is_deleted = 0",
      [account.name, account.position, account.hidden ? 1 : 0, account.id],
    );
  }

  async delete(id: number): Promise<void> {
    await this.execute(
      `
      UPDATE accounts
        SET is_deleted = (SELECT max(is_deleted) + 1 FROM accounts),
        updated_at = strftime('%s','now')
      WHERE _id = ? AND is_deleted = 0
      `,
      [id],
    );
  }

  // Balance operations
  async getBalance(
    accountId: number,
    currencyCode: string,
  ): Promise<{ amount: number; expenses: number; incomes: number }> {
    const result = await this.queryOne<{
      initial_balance: number;
      incomes_amount: number;
      expenses_amount: number;
      transfers_amount: number;
      exchanges_amount: number;
      creditors_amount: number;
      creditor_payments_amount: number;
      debtors_amount: number;
      debtor_payments_amount: number;
    }>(
      `
        SELECT
          d.initial_balance as amount,
          d.initial_balance,
          d.expenses_amount,
          d.incomes_amount,
          d.creditors_amount,
          d.creditor_payments_amount,
          d.debtors_amount,
          d.debtor_payments_amount,
          d.transfers_amount,
          d.exchanges_amount
        FROM account_balances AS d
        INNER JOIN currencies AS c
          ON d.currency_id == c._id
        INNER JOIN available_currencies AS cl
          ON c.available_currency_id == cl._id
        WHERE
          d.is_deleted == 0
          AND d.account_id == ?
          AND cl.ticker == ?
      `,
      [accountId, currencyCode],
    );

    let amount = 0;
    let incomes = 0;
    let expenses = 0;
    if (result) {
      // todo [ds]: move to business logic layer
      incomes = result.incomes_amount;

      expenses = result.expenses_amount;

      amount =
        // Initial balance
        result.initial_balance +
        // Currency exchange
        result.exchanges_amount +
        // Own transfers
        result.transfers_amount +
        // Incomes - Expenses
        result.incomes_amount -
        result.expenses_amount +
        // Credits
        result.creditors_amount -
        result.creditor_payments_amount +
        // Debits
        result.debtor_payments_amount -
        result.debtors_amount;
    }

    return { amount, incomes, expenses };
  }

  async findInitialBalances(accountId: number): Promise<{ currencyCode: string; amount: number; date: Date }[]> {
    const rows = await this.query<{ currencyCode: string; amount: number; unixDate: number }>(
      `SELECT cl.ticker as currencyCode, sb.initial_balance as amount, balance_date as unixDate
       FROM account_initial_balances sb
       INNER JOIN currencies c ON sb.currency_id = c._id
       INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
       WHERE sb.account_id = ?`,
      [accountId],
    );

    return rows.map((row) => ({
      currencyCode: row.currencyCode,
      amount: row.amount,
      date: new Date(row.unixDate * 1000),
    }));
  }

  async upsertInitialBalance(accountId: number, currencyCode: string, amount: number, date: Date): Promise<void> {
    const unixDate = new Date(date).setUTCHours(0, 0, 0, 0) / 1000;

    if (amount === 0) {
      // Delete the record if amount is 0
      await this.execute(
        `DELETE FROM account_initial_balances
         WHERE account_id = ?
         AND currency_id = (
           SELECT c._id
           FROM currencies c
           INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
           WHERE cl.ticker = ?
         )`,
        [accountId, currencyCode],
      );
    } else {
      // Insert-or-update using a two-step INSERT OR IGNORE + UPDATE pattern.
      //
      // • INSERT OR REPLACE is avoided because it fires the BEFORE DELETE
      //   trigger, which cascades DML through account_balances /
      //   account_daily_balances and can auto-roll-back the outer transaction
      //   in the SQLite WASM worker.
      //
      // • INSERT … ON CONFLICT DO UPDATE is avoided because it fires the
      //   UPDATE trigger whose nested INSERT OR IGNORE into account_daily_balances
      //   conflicts with the (account_id, currency_id, balance_date DESC)
      //   unique index in the sql.js environment.
      //
      // Two-step approach:
      //   1. INSERT OR IGNORE – fires INSERT trigger for fresh rows (creates
      //      the daily-balance scaffolding), silently skips existing rows.
      //   2. Plain UPDATE – fires UPDATE trigger which correctly transitions
      //      the aggregate tables from old to new values for ALL rows
      //      (including the one just inserted in step 1 where old = new).
      const currencySubquery = `(
            SELECT c._id
            FROM currencies c
            INNER JOIN available_currencies cl ON c.available_currency_id = cl._id
            WHERE cl.ticker = ?
          )`;

      await this.execute(
        `INSERT OR IGNORE INTO account_initial_balances (account_id, currency_id, initial_balance, balance_date, updated_at, is_deleted)
         VALUES (?, ${currencySubquery}, ?, ?, strftime('%s','now'), 0)`,
        [accountId, currencyCode, amount, unixDate],
      );

      await this.execute(
        `UPDATE account_initial_balances
         SET initial_balance = ?,
             balance_date    = ?,
             updated_at      = strftime('%s','now')
         WHERE account_id   = ?
           AND currency_id  = ${currencySubquery}
           AND is_deleted   = 0`,
        [amount, unixDate, accountId, currencyCode],
      );
    }
  }

  private mapRowToEntity(account: AccountRow): Account {
    return {
      ...account,
      hidden: Boolean(account.hidden),
    };
  }
}
